import { getHostConfig } from 'packages/helpers/hostConfig';

export const sanitizePathname = (path: string): string => {
    if (!path || !path.startsWith('/') || path.startsWith('//')) {
        return '/';
    }

    return path;
};

/**
 * Normalises a host-supplied `base-path` into the form react-router's `basename`
 * wants: a leading slash, no trailing slash, and `/` when there is no base path.
 *
 *     '/studio/' -> '/studio'      'studio' -> '/studio'      '' -> '/'
 */
export const normalizeBasePath = (value?: string | null): string => {
    if (!value) {
        return '/';
    }

    const withLeadingSlash = value.startsWith('/') ? value : `/${value}`;

    return withLeadingSlash.replace(/\/+$/, '') || '/';
};

const withLeadingSlash = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

/**
 * Removes the base path from a real `window.location.pathname`, giving a route
 * that react-router's `navigate()` will accept.
 *
 * Anything that reads `window.location.pathname` and feeds it back into the
 * router has to go through this, or the base path gets applied twice.
 */
export const stripBasePath = (pathname: string, basePath = getHostConfig().basePath): string => {
    const base = normalizeBasePath(basePath);

    if (base === '/') {
        return pathname;
    }

    if (pathname === base) {
        return '/';
    }

    return pathname.startsWith(`${base}/`) ? pathname.slice(base.length) : pathname;
};

/** Prefixes an in-app route with the base path, for real URL navigation. */
export const appPath = (path: string, basePath = getHostConfig().basePath): string => {
    const base = normalizeBasePath(basePath);
    const route = withLeadingSlash(path);

    return base === '/' ? route : `${base}${route}`;
};

/**
 * Builds an absolute, shareable URL for an in-app route.
 *
 * With a base path Studio owns a real path under the host, so the route goes
 * straight into the URL. Without one it is the iframe deployment, where the host
 * mirrors the fragment into the frame — hence the `#`.
 */
export const buildAppUrl = (hostUrl: string, path: string, basePath = getHostConfig().basePath): string => {
    const origin = (hostUrl || '').replace(/\/+$/, '');
    const base = normalizeBasePath(basePath);
    const route = withLeadingSlash(path);

    return base === '/' ? `${origin}/#${route}` : `${origin}${base}${route}`;
};

export const legacyHashRouteUrl = (location: Pick<Location, 'pathname' | 'hash'>): string | null => {
    if (!location.hash.startsWith('#/')) {
        return null;
    }

    const base = location.pathname.replace(/\/+$/, '');

    return `${base}${location.hash.slice(1)}`;
};
